import React, { useState, useEffect, useRef } from 'react';
import { Video, VideoOff, Mic, MicOff, Phone, PhoneOff, MessageSquare, Settings, Maximize, Minimize, Users, Clock, Camera, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TelemedicineRoom = ({ appointmentId, patientName, doctorName, onEnd }) => {
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('connecting'); // connecting, connected, disconnected
  const [remoteStream, setRemoteStream] = useState(null);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const containerRef = useRef(null);

  // Simulated connection
  useEffect(() => {
    // Simulate connection
    const connectTimeout = setTimeout(() => {
      setConnectionStatus('connected');
      toast.success('Connesso alla videochiamata');
    }, 2000);

    // Start local video
    startLocalVideo();

    return () => {
      clearTimeout(connectTimeout);
      stopLocalVideo();
    };
  }, []);

  // Call duration timer
  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    
    const timer = setInterval(() => {
      setCallDuration(d => d + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [connectionStatus]);

  const startLocalVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accessing media devices:', err);
      toast.error('Impossibile accedere alla fotocamera. Verifica i permessi.');
      setIsVideoOn(false);
      setIsAudioOn(false);
    }
  };

  const stopLocalVideo = () => {
    if (localVideoRef.current?.srcObject) {
      localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
  };

  const toggleVideo = () => {
    if (localVideoRef.current?.srcObject) {
      const videoTrack = localVideoRef.current.srcObject.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !isVideoOn;
        setIsVideoOn(!isVideoOn);
      }
    }
  };

  const toggleAudio = () => {
    if (localVideoRef.current?.srcObject) {
      const audioTrack = localVideoRef.current.srcObject.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !isAudioOn;
        setIsAudioOn(!isAudioOn);
      }
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    
    setChatMessages(prev => [...prev, {
      id: Date.now(),
      sender: 'me',
      text: newMessage,
      time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    }]);
    setNewMessage('');
  };

  const endCall = () => {
    stopLocalVideo();
    setConnectionStatus('disconnected');
    toast.info('Chiamata terminata');
    onEnd?.();
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full min-h-[600px] bg-gray-900 rounded-2xl overflow-hidden"
      data-testid="telemedicine-room"
    >
      {/* Connection Status */}
      {connectionStatus === 'connecting' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 z-50">
          <div className="animate-spin w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full mb-4" />
          <p className="text-white text-xl">Connessione in corso...</p>
          <p className="text-[#2A9FCB]/60 mt-2">In attesa del {doctorName || 'medico'}</p>
        </div>
      )}

      {connectionStatus === 'disconnected' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 z-50">
          <PhoneOff className="w-16 h-16 text-red-500 mb-4" />
          <p className="text-white text-xl">Chiamata terminata</p>
          <p className="text-[#2A9FCB]/60 mt-2">Durata: {formatDuration(callDuration)}</p>
        </div>
      )}

      {/* Main Video (Remote/Doctor) */}
      <div className="absolute inset-0">
        <video 
          ref={remoteVideoRef}
          autoPlay 
          playsInline
          className="w-full h-full object-cover"
          poster="/doctor-placeholder.jpg"
        />
        {/* Placeholder when no remote video */}
        {!remoteStream && connectionStatus === 'connected' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
            <div className="w-32 h-32 rounded-full bg-green-600 flex items-center justify-center text-5xl text-white font-bold mb-4">
              {doctorName?.[0] || 'D'}
            </div>
            <p className="text-white text-xl">{doctorName || 'Dott. in attesa'}</p>
            <p className="text-green-400 text-sm mt-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              Connesso
            </p>
          </div>
        )}
      </div>

      {/* Local Video (Patient/Self) */}
      <div className="absolute top-4 right-4 w-48 h-36 rounded-xl overflow-hidden border-2 border-white/30 shadow-lg z-20">
        <video 
          ref={localVideoRef}
          autoPlay 
          playsInline 
          muted
          className={`w-full h-full object-cover ${!isVideoOn ? 'hidden' : ''}`}
        />
        {!isVideoOn && (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <VideoOff className="w-8 h-8 text-[#2A9FCB]/70" />
          </div>
        )}
        <div className="absolute bottom-1 left-1 right-1 text-center">
          <span className="text-xs text-white bg-black/50 px-2 py-0.5 rounded">Tu</span>
        </div>
      </div>

      {/* Top Bar - Info */}
      <div className="absolute top-4 left-4 flex items-center gap-4 z-10">
        <div className="px-4 py-2 bg-black/50 backdrop-blur rounded-full  flex items-center gap-2 text-white">
          <Clock className="w-4 h-4" />
          <span className="font-mono">{formatDuration(callDuration)}</span>
        </div>
        {connectionStatus === 'connected' && (
          <div className="px-3 py-1 bg-green-500/20 border border-green-500 rounded-full  flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-green-400 text-sm">In corso</span>
          </div>
        )}
      </div>

      {/* Patient Name (for doctor) */}
      {patientName && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
          <div className="px-4 py-2 bg-black/50 backdrop-blur rounded-full  text-white">
            <Users className="w-4 h-4 inline mr-2" />
            {patientName}
          </div>
        </div>
      )}

      {/* Chat Panel */}
      {showChat && (
        <div className="absolute right-4 top-20 bottom-24 w-80 bg-white/95 backdrop-blur rounded-xl shadow-lg flex flex-col z-30">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold">Chat</h3>
            <button onClick={() => setShowChat(false)} className="text-[#2A9FCB]/70 hover:text-[#2A9FCB]">×</button>
          </div>
          <div className="flex-1 p-4 overflow-y-auto space-y-3">
            {chatMessages.map(msg => (
              <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-lg ${
                  msg.sender === 'me' ? 'bg-green-500 text-white' : 'bg-gray-200 text-[#2A9FCB]'
                }`}>
                  <p className="text-sm">{msg.text}</p>
                  <p className="text-xs opacity-70 mt-1">{msg.time}</p>
                </div>
              </div>
            ))}
            {chatMessages.length === 0 && (
              <p className="text-center text-[#2A9FCB]/60 text-sm">Nessun messaggio</p>
            )}
          </div>
          <div className="p-3 border-t flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Scrivi un messaggio..."
              className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <button 
              onClick={sendMessage}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            >
              Invia
            </button>
          </div>
        </div>
      )}

      {/* Bottom Controls */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
        <div className="flex items-center gap-3 px-6 py-4 bg-black/60 backdrop-blur-lg rounded-full ">
          {/* Microphone */}
          <button
            onClick={toggleAudio}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              isAudioOn ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-red-500 text-white'
            }`}
            title={isAudioOn ? 'Disattiva microfono' : 'Attiva microfono'}
            data-testid="toggle-mic"
          >
            {isAudioOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </button>

          {/* Camera */}
          <button
            onClick={toggleVideo}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              isVideoOn ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-red-500 text-white'
            }`}
            title={isVideoOn ? 'Disattiva video' : 'Attiva video'}
            data-testid="toggle-video"
          >
            {isVideoOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </button>

          {/* Speaker */}
          <button
            onClick={() => setIsSpeakerOn(!isSpeakerOn)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              isSpeakerOn ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-orange-500 text-white'
            }`}
            title={isSpeakerOn ? 'Muta speaker' : 'Attiva speaker'}
          >
            {isSpeakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>

          {/* End Call */}
          <button
            onClick={endCall}
            className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-all"
            title="Termina chiamata"
            data-testid="end-call"
          >
            <PhoneOff className="w-6 h-6" />
          </button>

          {/* Chat */}
          <button
            onClick={() => setShowChat(!showChat)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              showChat ? 'bg-green-500 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
            title="Chat"
            data-testid="toggle-chat"
          >
            <MessageSquare className="w-5 h-5" />
          </button>

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-all"
            title={isFullscreen ? 'Esci da schermo intero' : 'Schermo intero'}
          >
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

// Telemedicine Lobby Component
export const TelemedicineLobby = ({ appointment, onJoin, onCancel }) => {
  const [deviceCheck, setDeviceCheck] = useState({ camera: null, microphone: null });
  const [isTestingDevices, setIsTestingDevices] = useState(false);
  const previewVideoRef = useRef(null);

  const testDevices = async () => {
    setIsTestingDevices(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
      }
      
      setDeviceCheck({ camera: true, microphone: true });
      toast.success('Dispositivi funzionanti!');
      
      // Stop stream after test
      setTimeout(() => {
        stream.getTracks().forEach(track => track.stop());
      }, 5000);
    } catch (err) {
      console.error('Device test error:', err);
      setDeviceCheck({ camera: false, microphone: false });
      toast.error('Errore: ' + (err.message || 'Impossibile accedere ai dispositivi'));
    }
    setIsTestingDevices(false);
  };

  return (
    <div className="max-w-2xl mx-auto p-8 bg-white rounded-2xl shadow-lg" data-testid="telemedicine-lobby">
      <div className="text-center mb-8">
        <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
          <Video className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#2A9FCB]">Consulto in Telemedicina</h2>
        <p className="text-[#2A9FCB]/70 mt-2">Prepara il tuo dispositivo prima di entrare</p>
      </div>

      {/* Appointment Info */}
      {appointment && (
        <div className="mb-6 p-4 bg-gray-50 rounded-xl">
          <h3 className="font-semibold text-[#2A9FCB] mb-2">Dettagli Appuntamento</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-[#2A9FCB]/70">Data:</span>
              <span className="ml-2 font-medium">{appointment.date}</span>
            </div>
            <div>
              <span className="text-[#2A9FCB]/70">Ora:</span>
              <span className="ml-2 font-medium">{appointment.time}</span>
            </div>
            <div>
              <span className="text-[#2A9FCB]/70">Medico:</span>
              <span className="ml-2 font-medium">{appointment.doctor}</span>
            </div>
            <div>
              <span className="text-[#2A9FCB]/70">Motivo:</span>
              <span className="ml-2 font-medium">{appointment.reason}</span>
            </div>
          </div>
        </div>
      )}

      {/* Video Preview */}
      <div className="mb-6">
        <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden relative">
          <video
            ref={previewVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          {!deviceCheck.camera && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm opacity-70">Anteprima video</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Device Check */}
      <div className="mb-6 p-4 border rounded-xl">
        <h3 className="font-semibold text-[#2A9FCB] mb-3 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Verifica Dispositivi
        </h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Fotocamera
            </span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              deviceCheck.camera === null ? 'bg-gray-100 text-[#2A9FCB]/80' :
              deviceCheck.camera ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {deviceCheck.camera === null ? 'Non testata' : deviceCheck.camera ? '✓ OK' : '✗ Errore'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Mic className="w-4 h-4" />
              Microfono
            </span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              deviceCheck.microphone === null ? 'bg-gray-100 text-[#2A9FCB]/80' :
              deviceCheck.microphone ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {deviceCheck.microphone === null ? 'Non testato' : deviceCheck.microphone ? '✓ OK' : '✗ Errore'}
            </span>
          </div>
        </div>
        <button
          onClick={testDevices}
          disabled={isTestingDevices}
          className="mt-4 w-full py-2 border-2 border-green-500 text-green-600 rounded-xl font-medium hover:bg-green-50 disabled:opacity-50"
        >
          {isTestingDevices ? 'Verifica in corso...' : 'Verifica Dispositivi'}
        </button>
      </div>

      {/* Instructions */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <h4 className="font-semibold text-blue-800 mb-2">📋 Prima di iniziare</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Assicurati di avere una connessione internet stabile</li>
          <li>• Scegli un ambiente silenzioso e ben illuminato</li>
          <li>• Tieni a portata di mano i tuoi documenti medici</li>
          <li>• Consenti l'accesso a fotocamera e microfono</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <button
          onClick={onCancel}
          className="flex-1 py-3 border-2 border-gray-300 text-[#2A9FCB]/80 rounded-xl font-medium hover:bg-gray-50"
        >
          Annulla
        </button>
        <button
          onClick={onJoin}
          disabled={deviceCheck.camera === false}
          className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="join-call-btn"
        >
          <Video className="w-5 h-5" />
          Entra nella Chiamata
        </button>
      </div>
    </div>
  );
};

export default TelemedicineRoom;
